import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BottomTabs } from "@/components/BottomTabs";
import { LiveReactionsFeed } from "@/components/LiveReactionsFeed";
import {
  ArrowLeft, Target, Zap, Trophy, Heart, QrCode, TrendingUp,
  CalendarHeart, Sparkles, ListChecks, Crown, Loader2, HandCoins,
} from "lucide-react";

type Counts = Record<string, number>;

const SYSTEMS = [
  { key: "missions", label: "Fan Missions", desc: "Launch tasks. Reward fans.", icon: Target, table: "creator_missions", color: "from-orange-500 to-rose-500" },
  { key: "reactions", label: "Live Reactions", desc: "Tiered tip drops + animations.", icon: Zap, table: "live_reactions", color: "from-amber-400 to-pink-500" },
  { key: "level", label: "Creator Level", desc: "Powered by your Hustle Score.", icon: Trophy, table: null, color: "from-yellow-400 to-orange-500" },
  { key: "fans", label: "Fan Reputation", desc: "Loyalty score + supporter badges.", icon: Heart, table: "fan_reputation", color: "from-pink-500 to-fuchsia-500" },
  { key: "hunts", label: "QR Hunts", desc: "Hide rewards in the real world.", icon: QrCode, table: "qr_hunts", color: "from-emerald-500 to-teal-500" },
  { key: "fund", label: "Isle Fund", desc: "Crowdfund dreams across the isle.", icon: HandCoins, table: "isle_funds", color: "from-rose-500 to-orange-500" },
  { key: "momentum", label: "Momentum", desc: "Live trend score + status.", icon: TrendingUp, table: "creator_momentum", color: "from-indigo-500 to-violet-500" },
  { key: "events", label: "Live Events", desc: "Event mode with reward pools.", icon: CalendarHeart, table: "live_events", color: "from-sky-500 to-blue-600" },
  { key: "ai", label: "AI Hype Engine", desc: "Growth signals + suggestions.", icon: Sparkles, table: "ai_insights", color: "from-purple-500 to-indigo-500" },
  { key: "quests", label: "Quest Chains", desc: "Multi-step fan journeys.", icon: ListChecks, table: "creator_quests", color: "from-lime-500 to-emerald-500" },
  { key: "circles", label: "Creator Circles", desc: "Exclusive fan communities.", icon: Crown, table: "creator_circles", color: "from-amber-500 to-yellow-600" },
] as const;

const CreatorMode = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Counts>({});
  const [profile, setProfile] = useState<{ creator_level: number; creator_status: string; hustle_score: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: p } = await supabase
        .from("profiles")
        .select("creator_level, creator_status, hustle_score")
        .eq("user_id", user.id)
        .maybeSingle();
      if (p) setProfile(p as any);

      const next: Counts = {};
      await Promise.all(
        SYSTEMS.filter((s) => s.table).map(async (s) => {
          const { count } = await supabase
            .from(s.table as any)
            .select("*", { count: "exact", head: true })
            .eq("creator_id", user.id);
          next[s.key] = count ?? 0;
        }),
      );
      setCounts(next);
      setLoading(false);
    })();
  }, [user]);

  return (
    <MobileShell>
      <header className="bg-gradient-hero text-primary-foreground px-5 pt-8 pb-10">
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="mt-3">
          <h1 className="font-display text-2xl font-bold">Creator Mode</h1>
          <p className="text-sm opacity-90 mt-1">A live island economy for you and your fans.</p>
        </div>

        {profile && (
          <div className="mt-5 rounded-2xl bg-white/10 backdrop-blur-sm p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide opacity-75">Status</p>
              <p className="font-display text-lg font-semibold">{profile.creator_status}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide opacity-75">Level</p>
              <p className="font-display text-2xl font-bold">{profile.creator_level}</p>
              <p className="text-xs opacity-75">{profile.hustle_score} hustle pts</p>
            </div>
          </div>
        )}
      </header>

      <div className="px-5 -mt-4 pb-10 flex-1">
        {loading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {SYSTEMS.map((s) => {
              const Icon = s.icon;
              const count = s.table ? counts[s.key] ?? 0 : profile?.creator_level ?? 1;
              const ROUTES: Record<string, string> = {
                hunts: "/creator-mode/hunts",
                fund: "/isle-fund",
                missions: "/creator-mode/missions",
                events: "/creator-mode/events",
                quests: "/creator-mode/quests",
                circles: "/creator-mode/circles",
                fans: "/creator-mode/fans",
                momentum: "/creator-mode/momentum",
                ai: "/creator-mode/ai",
              };
              const route = ROUTES[s.key] ?? null;
              const Wrapper: any = route ? Link : "div";
              const wrapperProps = route ? { to: route } : {};
              return (
                <Wrapper
                  key={s.key}
                  {...wrapperProps}
                  className="rounded-2xl bg-background shadow-card p-4 flex flex-col gap-2 active:scale-[0.98] transition-smooth"
                >
                  <div
                    className={`h-10 w-10 rounded-xl bg-gradient-to-br ${s.color} grid place-items-center text-white`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{s.label}</p>
                    <p className="text-xs text-muted-foreground leading-tight mt-0.5">{s.desc}</p>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-muted-foreground">
                      {s.table ? `${count} active` : `Lvl ${count}`}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">
                      {route ? "Open" : "Soon"}
                    </span>
                  </div>
                </Wrapper>
              );
            })}
          </div>
        )}

        {user && (
          <div className="mt-6">
            <LiveReactionsFeed creatorId={user.id} />
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-muted/40 p-4 text-xs text-muted-foreground leading-relaxed">
          <p className="font-semibold text-foreground mb-1">Scaffold ready</p>
          Database tables, access rules, and realtime channels are live for all 10 systems.
          Each card above will get a dedicated builder + fan view next.{" "}
          <Link to="/points" className="text-primary font-medium">View hustle activity →</Link>
        </div>
      </div>

      <BottomTabs />
    </MobileShell>
  );
};

export default CreatorMode;
